/**
 * Composable pour gérer les appels API avec état de chargement et erreurs
 * 
 * Centralise tous les appels HTTP avec gestion automatique du loading, des erreurs et des toasts.
 * Remplace les appels fetch() directs dans les composables.
 * 
 * @example
 * // Utilisation simple
 * const { loading, get, post } = useApi()
 * const data = await get('/api/users')
 * await post('/api/users', { name: 'John' })
 * 
 * @example
 * // Avec options personnalisées
 * const { get } = useApi()
 * const data = await get('/api/data', {
 *   showErrorToast: false,
 *   errorMessage: 'Erreur personnalisée'
 * })
 * 
 * @example
 * // Helpers CRUD
 * const { list, detail, create, update, remove } = useApi()
 * const users = await list('/api/users', { page: 1 })
 * const user = await detail('/api/users', 123)
 */
import { ref } from 'vue'
import { toast } from 'vue-sonner'
import { api } from '../services/api.js'
import { translateError, isRetryableError } from '../utils/errorMessages.js'
import { usePostHog } from './usePostHog.js'

export function useApi() {
  const loading = ref(false)
  const error = ref(null)
  const { captureException } = usePostHog()

  /**
   * Exécute une requête API avec gestion automatique du loading et des erreurs
   * @param {Function} requestFn - Fonction qui retourne une Promise (appel API)
   * @param {Object} options - Options de configuration
   * @param {boolean} options.showErrorToast - Afficher un toast en cas d'erreur (défaut: true)
   * @param {boolean} options.showSuccessToast - Afficher un toast en cas de succès (défaut: false)
   * @param {string} options.successMessage - Message de succès à afficher
   * @param {string} options.errorMessage - Message d'erreur personnalisé
   * @returns {Promise<*>} - Résultat de la requête
   */
  const execute = async (requestFn, options = {}) => {
    const {
      showErrorToast = true,
      showSuccessToast = false,
      successMessage = 'Opération réussie',
      errorMessage = null
    } = options

    loading.value = true
    error.value = null

    try {
      const result = await requestFn()
      
      if (showSuccessToast) {
        toast.success(successMessage, {
          description: options.successDescription || undefined
        })
      }
      
      return result
    } catch (err) {
      let message = errorMessage
      let description = options.errorDescription
      
      if (!message) {
        const translated = translateError(err, {
          action: options.action || 'effectuer cette action',
          resource: options.resource || 'la ressource'
        })
        message = translated.title
        description = description || translated.description
      }
      
      error.value = message
      
      // Capture API error in PostHog with context
      const errorContext = {
        error_type: 'API Error',
        http_status: err.response?.status || err.status || null,
        error_message: message,
        error_description: description || null,
        url: options.url || null,
        method: options.method || null
      }
      captureException(err, errorContext)
      
      if (showErrorToast) {
        const canRetry = isRetryableError(err) && (options.showRetry !== false)
        
        toast.error(message, {
          description: description || undefined,
          action: canRetry ? {
            label: 'Réessayer',
            onClick: () => {
              if (options.onRetry) {
                options.onRetry()
              } else {
                // Réessayer automatiquement la même requête
                execute(requestFn, { ...options, showErrorToast: false })
                  .catch(() => {}) // Ignorer les erreurs du retry pour éviter les boucles
              }
            }
          } : undefined
        })
      }
      
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Wrapper pour les méthodes API courantes
   */
  const get = async (url, options = {}) => {
    return execute(() => api.get(url, options), { ...options, url, method: 'GET' })
  }

  const post = async (url, body, options = {}) => {
    return execute(() => api.post(url, body, options), { ...options, url, method: 'POST' })
  }

  const put = async (url, body, options = {}) => {
    return execute(() => api.put(url, body, options), { ...options, url, method: 'PUT' })
  }

  const patch = async (url, body, options = {}) => {
    return execute(() => api.patch(url, body, options), { ...options, url, method: 'PATCH' })
  }

  const del = async (url, options = {}) => {
    return execute(() => api.delete(url, options), { ...options, url, method: 'DELETE' })
  }

  /**
   * Réinitialise l'état d'erreur
   */
  const clearError = () => {
    error.value = null
  }

  /**
   * Helpers pour les patterns CRUD courants
   */
  const list = async (url, params = {}, options = {}) => {
    const queryString = new URLSearchParams(params).toString()
    const fullUrl = queryString ? `${url}?${queryString}` : url
    return get(fullUrl, options)
  }

  const detail = async (url, id, options = {}) => {
    return get(`${url}/${id}`, options)
  }

  const create = async (url, body, options = {}) => {
    return post(url, body, options)
  }

  const update = async (url, id, body, options = {}) => {
    return patch(`${url}/${id}`, body, options)
  }

  const remove = async (url, id, options = {}) => {
    return del(`${url}/${id}`, options)
  }

  return {
    loading,
    error,
    execute,
    get,
    post,
    put,
    patch,
    delete: del,
    clearError,
    // Helpers CRUD
    list,
    detail,
    create,
    update,
    remove
  }
}

